import { useEffect } from 'react';
import { QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/useAuthStore';
import { useProfileStore } from '../store/useProfileStore';
import { useActivityStore } from '../store/useActivityStore';
import { notify } from './notify';
import { logError, logDebug } from './loggerService';

// Injected by InitWs so the event handler can invalidate queries without
// importing the QueryClient from App.tsx (which would create a circular dep).
let queryClient: QueryClient | null = null;

export function setWsQueryClient(qc: QueryClient) {
  queryClient = qc;
}

export interface OrderWsEvent {
  type: string;
  orderId: string;
}

type OrderEventListener = (event: OrderWsEvent) => void;

const orderEventListeners = new Set<OrderEventListener>();

export function subscribeToOrderEvents(listener: OrderEventListener): () => void {
  orderEventListeners.add(listener);
  return () => {
    orderEventListeners.delete(listener);
  };
}

// Cloud: envs set VITE_WS_BASE_URL to the public WS LB origin (e.g.
//       wss://ws.staging.homeplated.com), bypassing Firebase Hosting since it doesn't proxy WebSocket upgrades.
// Local: leaves VITE_WS_BASE_URL unset, so we fall back to window.location and Vite's `/ws` proxy
//        (vite.config.ts) routes the upgrade to the BE on localhost.
// Computed lazily so module evaluation never reads window.location — if that
// access throws at import time (rare iOS WebView edge cases), the whole
// bundle dies before any ErrorBoundary can mount.
function getWsBaseUrl(): string {
  const envUrl = import.meta.env.VITE_WS_BASE_URL;
  if (envUrl) return envUrl;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
}

const INITIAL_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;
const RECONNECT_MULTIPLIER = 2;

interface WsConnection {
  ws: WebSocket | null;
  reconnectDelay: number;
  reconnectTimeout: ReturnType<typeof setTimeout> | null;
  intentionalClose: boolean;
}

const connections: Record<string, WsConnection> = {
  activityCount: { ws: null, reconnectDelay: INITIAL_RECONNECT_DELAY, reconnectTimeout: null, intentionalClose: false },
  events: { ws: null, reconnectDelay: INITIAL_RECONNECT_DELAY, reconnectTimeout: null, intentionalClose: false },
};

/**
 * Handle activity count WebSocket messages.
 */
function handleActivityCount(data: string) {
  const count = parseInt(data, 10);
  if (isNaN(count)) {
    logError(new Error(`Invalid activity count: ${data}`), 'Failed to parse activity count');
    return;
  }

  logDebug(`Activity count update: ${count}`);
  useActivityStore.getState().setActivityCount(count);
  if (count > 0) {
    notify.info(`You have ${count} unread ${count === 1 ? 'activity' : 'activities'}`, { id: 'ws:activity-count' });
  } else {
    notify.dismiss('ws:activity-count');
  }
}

/**
 * Handle event WebSocket messages.
 */
function handleEvent(data: string) {
  try {
    const event = JSON.parse(data) as { type: string; id: string };
    logDebug('Event received', undefined, event);

    // Starter baseline: no per-event toasts are wired here. Per-app code that
    // owns specific event surfaces (orders, listings, etc.) adds its own cases
    // back or subscribes via `subscribeToOrderEvents`. Keeping the switch in
    // place so the per-app delta is "add cases" rather than "rebuild the
    // dispatcher".
    switch (event.type) {
      default:
        // Unknown event types: log only. Per-app code is expected to handle
        // the events it cares about (either by adding a case above or via
        // `subscribeToOrderEvents`); order_* events still get fanned out
        // below regardless.
        if (!event.type.startsWith('order_')) {
          logDebug(`Unhandled WS event type: ${event.type}`);
        }
        break;
    }

    // Invalidate order queries so any open order page re-fetches immediately.
    if (event.type.startsWith('order_') && queryClient) {
      // event.id is the orderId for order events
      queryClient.invalidateQueries({ queryKey: ['order', event.id] });
      // Also refresh listing-orders lists (lister's listing detail page)
      queryClient.invalidateQueries({ queryKey: ['listing-orders'] });

      // Fan out to UI subscribers (e.g., verification-window auto-pop).
      orderEventListeners.forEach((listener) => {
        try {
          listener({ type: event.type, orderId: event.id });
        } catch (err) {
          logError(err instanceof Error ? err : new Error(String(err)), 'Order event listener threw');
        }
      });
    }
  } catch (error) {
    logError(error instanceof Error ? error : new Error(String(error)), 'Failed to parse event message');
  }
}

/**
 * Create a WebSocket connection with reconnection logic.
 */
function createConnection(key: string, url: string, onMessage: (data: string) => void, onOpen?: () => void): void {
  const conn = connections[key];
  if (!conn) return;

  conn.intentionalClose = false;

  try {
    const ws = new WebSocket(url);

    ws.onopen = () => {
      logDebug(`WebSocket connected: ${key}`);
      conn.reconnectDelay = INITIAL_RECONNECT_DELAY;
      onOpen?.();
    };

    ws.onmessage = (event) => {
      onMessage(event.data as string);
    };

    ws.onclose = () => {
      logDebug(`WebSocket closed: ${key}`);
      conn.ws = null;

      if (!conn.intentionalClose && useAuthStore.getState().isAuthenticated) {
        scheduleReconnect(key, url, onMessage, onOpen);
      }
    };

    ws.onerror = () => {
      logError(new Error('WebSocket error'), `WebSocket error: ${key}`);
      ws.close();
    };

    conn.ws = ws;
  } catch (error) {
    logError(error instanceof Error ? error : new Error(String(error)), `Failed to create WebSocket: ${key}`);
    scheduleReconnect(key, url, onMessage);
  }
}

/**
 * Schedule a reconnection with exponential backoff.
 */
function scheduleReconnect(key: string, url: string, onMessage: (data: string) => void, onOpen?: () => void): void {
  const conn = connections[key];
  if (!conn || conn.intentionalClose) return;

  logDebug(`Reconnecting ${key} in ${conn.reconnectDelay}ms`);

  conn.reconnectTimeout = setTimeout(() => {
    conn.reconnectTimeout = null;
    createConnection(key, url, onMessage, onOpen);
  }, conn.reconnectDelay);

  conn.reconnectDelay = Math.min(conn.reconnectDelay * RECONNECT_MULTIPLIER, MAX_RECONNECT_DELAY);
}

/**
 * Close a single WebSocket connection and cancel any pending reconnect.
 */
function closeConnection(key: string): void {
  const conn = connections[key];
  if (!conn) return;

  conn.intentionalClose = true;

  if (conn.reconnectTimeout) {
    clearTimeout(conn.reconnectTimeout);
    conn.reconnectTimeout = null;
  }

  if (conn.ws) {
    if (conn.ws.readyState === WebSocket.OPEN) {
      conn.ws.close();
    }
    conn.ws = null;
  }

  conn.reconnectDelay = INITIAL_RECONNECT_DELAY;
}

/**
 * Connect to both WebSocket topics for the given profile.
 */
function connect(profileId: string): void {
  disconnectAll();

  const wsBaseUrl = getWsBaseUrl();
  const activityUrl = `${wsBaseUrl}/ws/activity/count/${profileId}`;
  const eventsUrl = `${wsBaseUrl}/ws/event/${profileId}`;

  logDebug(`Connecting WebSockets for profile: ${profileId}`);

  createConnection('activityCount', activityUrl, handleActivityCount);
  createConnection('events', eventsUrl, handleEvent);
}

/**
 * Close all WebSocket connections.
 * Called on logout and when profile changes.
 */
export function disconnectAll(): void {
  closeConnection('activityCount');
  closeConnection('events');
}

/**
 * Renderless component that manages WebSocket connections.
 * Connects when authenticated with an active profile.
 * Disconnects and reconnects on profile change.
 * Disconnects on logout.
 */
export function InitWs() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Select the id only — `activeProfile` object identity changes on every
  // profile refetch even when the underlying profile is the same, which would
  // otherwise tear down and re-establish the WS connections on each refetch
  // and replay the server's initial messages (the original "triple toast"
  // root cause).
  const profileId = useProfileStore((s) => s.activeProfile?.id);

  useEffect(() => {
    if (isAuthenticated && profileId) {
      connect(profileId);
    }
    return () => disconnectAll();
  }, [isAuthenticated, profileId]);

  return null;
}
